import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Mail, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/admin/submissions/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const resend = async () => {
    setBusy("resend");
    try {
      const r = await api.post(`/admin/submissions/${id}/resend`);
      if (r.data.sent) toast.success(`Report emailed to ${r.data.to}.`);
      else if (r.data.mocked) toast.success(`Report queued to ${r.data.to} (email delivery being finalised).`);
      else toast.error(`Could not send to ${r.data.to}.`);
    } catch (e) { toast.error("Failed."); } finally { setBusy(""); }
  };
  const regenerate = async () => {
    setBusy("regen");
    try { await api.post(`/admin/submissions/${id}/regenerate`); toast.success("PDF regenerated."); }
    catch (e) { toast.error("Failed."); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("dl");
    try {
      const res = await api.get(`/assessment/${id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `Aikyam_${data.report_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No report."); } finally { setBusy(""); }
  };

  if (!data) return <div className="text-[var(--text2)]"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const r = data.respondent_full || {};
  const s = data.scores || {};

  return (
    <div data-testid="submission-detail">
      <button onClick={() => navigate("/admin/submissions")} className="flex items-center gap-2 text-sm text-[var(--text2)] hover:text-oxford mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to submissions
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="overline mb-1">{data.report_id || "In progress"} · Workbook v{data.workbook_version}</div>
          <h1 className="font-serif text-4xl text-oxford tracking-tight">{r.name}</h1>
          <p className="text-[var(--text2)] mt-1">{r.company} · {r.sector} · {r.designation}</p>
          <p className="text-[var(--text2)] text-sm mt-1">{r.email} · {r.phone}</p>
        </div>
        {data.status === "completed" && (
          <div className="flex flex-wrap gap-3">
            <Button data-testid="detail-download-btn" onClick={download} disabled={busy} className="btn-outline h-10 px-5"><Download className="mr-2 h-4 w-4" /> PDF</Button>
            <Button data-testid="detail-resend-btn" onClick={resend} disabled={busy} className="btn-outline h-10 px-5">
              {busy === "resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" /> Resend</>}
            </Button>
            <Button data-testid="detail-regen-btn" onClick={regenerate} disabled={busy} className="btn-outline h-10 px-5">
              {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-2 h-4 w-4" /> Regenerate</>}
            </Button>
          </div>
        )}
      </div>

      {data.status === "completed" ? (
        <>
          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            <ScoreCard label="Business Score" value={s.business_score} />
            <ScoreCard label="Legacy Score" value={s.legacy_score} accent />
          </div>

          {(data.responses || []).map((comp) => (
            <div key={comp.component} className="bg-white border border-[var(--brdr)] rounded-sm shadow-sm mt-6">
              <div className="p-5 border-b border-[var(--brdr)] flex items-center justify-between">
                <h3 className="font-serif text-xl text-oxford">{comp.component}{comp.is_legacy ? " · Legacy" : ""}</h3>
                <div className="flex items-center gap-4 font-mono text-sm">
                  <span className="text-[var(--text2)]">Weight {comp.component_weight}</span>
                  <span className="text-oxford">Score {comp.component_score?.toFixed(1) ?? "—"}</span>
                </div>
              </div>
              <div className="divide-y divide-[var(--brdr)]">
                {comp.questions.map((q) => (
                  <div key={q.qid} className="px-5 py-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-[var(--text2)]">{q.qid}{q.dimension ? ` · ${q.dimension}` : ""}</span>
                      <span className="text-xs font-mono text-[var(--text2)]">QW {q.question_weight}</span>
                    </div>
                    <p className="text-sm text-oxford mt-1">{q.question}</p>
                    <div className="mt-2 flex items-center gap-3">
                      <span className="text-sm text-[var(--text2)]">{q.selected_option || "—"}</span>
                      {q.selected_option_weight !== null && q.selected_option_weight !== undefined && (
                        <span className="text-xs font-mono bg-alabaster border border-[var(--brdr)] rounded px-2 py-0.5">OW {q.selected_option_weight}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="bg-white border border-[var(--brdr)] rounded-sm p-10 mt-8 text-center text-[var(--text2)]">
          This assessment is still in progress and has not been submitted.
        </div>
      )}
    </div>
  );
}

const ScoreCard = ({ label, value, accent }) => (
  <div className="bg-white border border-[var(--brdr)] rounded-sm p-8 shadow-sm">
    <div className="overline">{label}</div>
    <div className="font-serif text-6xl mt-3" style={{ color: accent ? "#C5A880" : "#0A1128" }}>
      {value === null || value === undefined ? "—" : value.toFixed(1)}
      <span className="font-mono text-base text-[var(--text2)] ml-1">/ 10</span>
    </div>
  </div>
);
